import type { CSSProperties } from 'react';
import { getBezierPath, type EdgeProps } from '@xyflow/react';
import { DEPTH_COLORS, DIMMED_OPACITY, TRANSITION_DURATION } from './mindmap-constants';
import type { MindmapEdgeData, MindmapFlowEdge } from './mindmap-types';

const MINDMAP_EDGE_COLOR_ALPHA = '66';
const MINDMAP_EDGE_ENTRANCE_DEPTH_DELAY_MS = 80;
const MINDMAP_EDGE_ENTRANCE_DEPTH_OFFSET = 0.5;
const MINDMAP_EDGE_ENTRANCE_DURATION_MS = 400;
const MINDMAP_EDGE_INITIAL_OPACITY = 0;
const MINDMAP_EDGE_VISIBLE_OPACITY = 1;
const MINDMAP_EDGE_POINTER_EVENTS = 'none';

enum MindmapEdgeActivityState {
  Active = 'active',
  Dimmed = 'dimmed',
}

enum MindmapEdgeEntrancePhase {
  Complete = 'complete',
  Entering = 'entering',
}

type MindmapEdgePosition = EdgeProps<MindmapFlowEdge>['sourcePosition'];

interface MindmapEdgeModelInput {
  sourceNodeId: string;
  targetNodeId: string;
  sourceX: number;
  sourceY: number;
  sourcePosition: MindmapEdgePosition;
  targetX: number;
  targetY: number;
  targetPosition: MindmapEdgePosition;
  edgeData: MindmapEdgeData | undefined;
  hoveredNodeId: string | null;
  highlightedNodeIds: ReadonlySet<string>;
  didEntranceStart: boolean;
  isEntranceComplete: boolean;
}

interface MindmapEdgeTransitionInput {
  entrancePhase: MindmapEdgeEntrancePhase;
  entranceDelayMs: number;
}

interface MindmapEdgeActivityInput {
  sourceNodeId: string;
  targetNodeId: string;
  hoveredNodeId: string | null;
  highlightedNodeIds: ReadonlySet<string>;
}

export interface MindmapEdgeModel {
  groupStyle: CSSProperties;
  path: string;
  strokeColor: string;
}

const MINDMAP_EDGE_TARGET_OPACITY_BY_ACTIVITY: Record<MindmapEdgeActivityState, number> = {
  [MindmapEdgeActivityState.Active]: MINDMAP_EDGE_VISIBLE_OPACITY,
  [MindmapEdgeActivityState.Dimmed]: DIMMED_OPACITY,
};

const MINDMAP_EDGE_TRANSITION_BY_PHASE: Record<
  MindmapEdgeEntrancePhase,
  (input: { entranceDelayMs: number }) => string
> = {
  [MindmapEdgeEntrancePhase.Complete]: () => `opacity ${TRANSITION_DURATION} ease-out`,
  [MindmapEdgeEntrancePhase.Entering]: ({ entranceDelayMs }) =>
    `opacity ${MINDMAP_EDGE_ENTRANCE_DURATION_MS}ms ease-out ${entranceDelayMs}ms`,
};

export function getMindmapEdgeModel(input: MindmapEdgeModelInput): MindmapEdgeModel {
  const activityState = selectMindmapEdgeActivityState({
    sourceNodeId: input.sourceNodeId,
    targetNodeId: input.targetNodeId,
    hoveredNodeId: input.hoveredNodeId,
    highlightedNodeIds: input.highlightedNodeIds,
  });
  const entrancePhase = selectMindmapEdgeEntrancePhase(input.isEntranceComplete);
  const targetOpacity = MINDMAP_EDGE_TARGET_OPACITY_BY_ACTIVITY[activityState];

  return {
    groupStyle: {
      pointerEvents: MINDMAP_EDGE_POINTER_EVENTS,
      opacity: input.didEntranceStart ? targetOpacity : MINDMAP_EDGE_INITIAL_OPACITY,
      transition: getMindmapEdgeTransition({
        entrancePhase,
        entranceDelayMs: getMindmapEdgeEntranceDelayMs(input.edgeData),
      }),
    },
    path: getMindmapEdgePath(input),
    strokeColor: `${getMindmapEdgeColor(input.edgeData)}${MINDMAP_EDGE_COLOR_ALPHA}`,
  };
}

function selectMindmapEdgeActivityState(input: MindmapEdgeActivityInput): MindmapEdgeActivityState {
  if (input.hoveredNodeId == null) {
    return MindmapEdgeActivityState.Active;
  }

  return getIsMindmapEdgeHighlighted(input)
    ? MindmapEdgeActivityState.Active
    : MindmapEdgeActivityState.Dimmed;
}

function getIsMindmapEdgeHighlighted(input: MindmapEdgeActivityInput): boolean {
  return (
    input.highlightedNodeIds.has(input.sourceNodeId) ||
    input.highlightedNodeIds.has(input.targetNodeId)
  );
}

function selectMindmapEdgeEntrancePhase(isEntranceComplete: boolean): MindmapEdgeEntrancePhase {
  return isEntranceComplete ? MindmapEdgeEntrancePhase.Complete : MindmapEdgeEntrancePhase.Entering;
}

function getMindmapEdgeTransition(input: MindmapEdgeTransitionInput): string {
  return MINDMAP_EDGE_TRANSITION_BY_PHASE[input.entrancePhase]({
    entranceDelayMs: input.entranceDelayMs,
  });
}

function getMindmapEdgeEntranceDelayMs(edgeData: MindmapEdgeData | undefined): number {
  return ((edgeData?.sourceDepth ?? 0) + MINDMAP_EDGE_ENTRANCE_DEPTH_OFFSET) *
    MINDMAP_EDGE_ENTRANCE_DEPTH_DELAY_MS;
}

function getMindmapEdgeColor(edgeData: MindmapEdgeData | undefined): string {
  const colorIndex = edgeData?.colorIndex ?? 0;

  return DEPTH_COLORS[colorIndex] ?? DEPTH_COLORS[0];
}

function getMindmapEdgePath(input: MindmapEdgeModelInput): string {
  const [path] = getBezierPath({
    sourceX: input.sourceX,
    sourceY: input.sourceY,
    sourcePosition: input.sourcePosition,
    targetX: input.targetX,
    targetY: input.targetY,
    targetPosition: input.targetPosition,
  });

  return path;
}
